<template>
  <kks-vgbm-form
    v-if="!isNotFoundError && !isInvalidLocationError"
    :pending-vgbm-classes="pendingVgbmClasses"
    :vgbm-assignments="vgbmAssignments"
    @assignment-changed="handleAssignmentChanged"
  />
  <div v-else-if="isNotFoundError" class="text-center">
    <h3>
      {{
        t('kksVgbmForm.errors.kksDoesNotExist', {
          kks: kksCode,
          location,
        })
      }}
    </h3>
  </div>
  <div v-else-if="isInvalidLocationError" class="text-center">
    <h3>{{ $tt('kksVgbmForm.errors.invalidLocation') }}</h3>
  </div>
</template>

<script lang="ts" setup>
  import { onMounted, ref, watch } from 'vue';
  import {
    AssignmentUpdateEvent,
    FilledVgbmAssignment,
  } from '@/components/KksVgbmForm/KksVgbmForm.vue';
  import { ProjectDataService, VgbmService } from '@/services';
  import {
    IblImportWarningType,
    IGetVgbmAssignmentResponseDto,
    IUpdateVgbmAssignmentResponseDto,
  } from 'types';
  import { HttpStatusCode, isAxiosError } from 'axios';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import { Route, routeParams } from '@/routes';

  const { kksIdProp = null, kksCodeWithLocation = null } = defineProps<{
    kksIdProp?: string;
    kksCodeWithLocation?: string;
  }>();

  const { t } = useI18n();
  const { createErrorToast, createSuccessToast, createWarningToast } = useToast();

  const emit = defineEmits<{
    (e: 'hide'): void;
  }>();
  defineExpose({ resetForm });

  const isFetchingVgbmAssignments = ref(false);
  const isInvalidLocationError = ref(false);
  const isNotFoundError = ref(false);
  const kksCode = ref<string | null>(null);
  const kksId = ref<string | null>(null);
  const location = ref<string | null>(null);
  const pendingVgbmClasses = ref<string[]>([]);
  const vgbmAssignments = ref<IGetVgbmAssignmentResponseDto[]>([]);

  async function fetchVgbmAssignments(): Promise<void> {
    if (!kksId.value) return;
    try {
      isFetchingVgbmAssignments.value = true;
      const { kks, vgbmAssignments: assignments } =
        await VgbmService.getVgbmAssignmentsForKks(kksId.value);
      kksCode.value = kks;
      vgbmAssignments.value = assignments;
    } catch {
      createErrorToast(t('kksVgbmModal.errors.errorGettingAssignments'));
    } finally {
      isFetchingVgbmAssignments.value = false;
    }
  }

  async function updateAssignment(
    assignment: FilledVgbmAssignment,
  ): Promise<void> {
    if (!kksId.value) return;
    try {
      pendingVgbmClasses.value = [
        ...pendingVgbmClasses.value,
        assignment.class,
      ];
      const updatedAssignment = await VgbmService.updateVgbmAssignmentsForKks(
        assignment._id,
        assignment,
      );
      if (updatedAssignment.warning) {
        displayWarningIfPresent(updatedAssignment);
      } else {
        createSuccessToast(t('kksVgbmModal.assignmentUpdated'));
      }
    } catch {
      createErrorToast(t('kksVgbmModal.errors.errorSavingAssignment'));
    } finally {
      pendingVgbmClasses.value = pendingVgbmClasses.value.filter(
        (c) => c !== assignment.class,
      );
    }
  }

  async function handleAssignmentChanged({
    assignment,
    isValid,
  }: AssignmentUpdateEvent) {
    const index = VgbmService.findAssignmentIndexByClass(
      vgbmAssignments.value,
      assignment.class,
    );
    if (index === -1) return;
    vgbmAssignments.value[index] = assignment;

    if (isValid) {
      await updateAssignment(assignment);
    }
  }

  function displayWarningIfPresent(
    updateResponse: IUpdateVgbmAssignmentResponseDto,
  ) {
    if (updateResponse.warning) {
      createWarningToast(
        t('kksVgbmModal.warnings.invalidUnit', {
          unit: updateResponse.warning,
        }),
      );
    }
  }

  function resetForm(): void {
    isFetchingVgbmAssignments.value = false;
    kksCode.value = null;
    pendingVgbmClasses.value = [];
    vgbmAssignments.value = [];
    emit('hide');
  }

  async function getKksCodeAndLocationFromUrl(): Promise<void> {
    if (!kksCodeWithLocation) return;
    const locations =
      await ProjectDataService.getPossibleProjectLocationsForSelect();
    location.value =
      locations
        .map((loc) => loc.value)
        .find((loc) => kksCodeWithLocation.startsWith(`${loc}-`)) || null;
    if (!location.value) {
      isInvalidLocationError.value = true;
      return;
    }
    kksCode.value = kksCodeWithLocation
      ? kksCodeWithLocation.replace(`${location.value}-`, '').trim()
      : null;
  }

  async function resolveKksId(): Promise<void> {
    await getKksCodeAndLocationFromUrl();
    if (kksIdProp || !kksCode.value || !location.value) {
      kksId.value = kksIdProp;
      return;
    }
    try {
      kksId.value = await VgbmService.resolveKksId(
        kksCode.value,
        location.value,
      );
    } catch (e) {
      if (
        isAxiosError(e) &&
        e.response?.status === HttpStatusCode.Unauthorized
      ) {
        return;
      } else if (
        isAxiosError(e) &&
        e.response?.status === HttpStatusCode.NotFound
      ) {
        isNotFoundError.value = true;
      } else {
        createErrorToast(
          t('kksVgbmForm.errors.couldNotGetKks', {
            kks: kksCode.value,
          }),
        );
      }
    }
  }

  onMounted(resolveKksId);

  watch(kksId, (newKksId) => {
    if (newKksId) {
      fetchVgbmAssignments();
    }
  });

  const route = useRoute();
  watch(
    () => route.params[routeParams[Route.KKS_VGBM_FORM].kksCodeWithLocation],
    () => {
      window.location.reload();
    },
  );
</script>
